import type { XandrKeywords } from './types';

/**
 * @example
 * input { keyword1: 'value1', keyword2: undefined, keyword3: [] }
 * output { keyword1: 'value1' }
 */
export const cleanupKeywordsObject = (values: Partial<XandrKeywords>) =>
  Object.entries(values)
    // exclude undefined values
    .filter(
      (pair): pair is [string, XandrKeywords[keyof XandrKeywords]] =>
        pair[1] !== undefined && pair[1] !== null
    )
    // exclude empty array values
    .filter(([, value]) => !(Array.isArray(value) && value.length === 0))
    .reduce(
      (keywords, [key, value]) => ({ ...keywords, [key]: value }),
      {} as XandrKeywords
    );

const PREFIXED_KEYWORD_STARTS = ['no-', 'aa-', 'se-'];

/**
 * Renames `no-`, `aa-` and `se-` keywords from EXTERNAL inputs (URL `kv_*`
 * query-string and host-supplied `ads.keywords`) to their `kw_`-prefixed form.
 * Internal static keywords are defined with `kw_` at source and never need
 * this. Other keys (including already `kw_`-prefixed ones) pass through.
 *
 * @example
 * input { 'aa-keyword2': 'value2', keyword3: 'value3' }
 * output { 'kw_aa-keyword2': 'value2', keyword3: 'value3' }
 */
export const prefixExternalKeywords = <T extends Record<string, unknown>>(
  values: T
) =>
  Object.keys(values).reduce(
    (keywords, key) => {
      const shouldPrefix = PREFIXED_KEYWORD_STARTS.some(
        (start) => key.indexOf(start) === 0
      );

      keywords[shouldPrefix ? `kw_${key}` : key] = values[key];

      return keywords;
    },
    {} as Record<string, unknown>
  ) as { [key: string]: T[keyof T] };

export const prepareXandrKeywords = (values: Partial<XandrKeywords>) =>
  cleanupKeywordsObject(values);
